/**
 * THE LEG-LENGTH SCALE — how a clip authored on one man's legs is worn by
 * another man's.
 *
 * ── WHAT THE CLIPS ACTUALLY CARRY ──────────────────────────────────────────
 *
 * `stripConstantBoneTranslation` drops 73 of the 74 absolute translation tracks
 * and deliberately keeps one: `hips`, because that track is the root motion.
 * But it is absolute too — it writes the MASTER's hip height, in metres, onto
 * whoever is wearing the clip. See AUTHORED_MASTER_HIPS_REST_Y.
 *
 * ── WHY THE CONSTANT SHIFT THAT WAS THERE IS NOT ENOUGH ────────────────────
 *
 * The previous correction lifted the whole character by
 * `hisRestHips - masterRestHips`. That is EXACT at rest and wrong at every
 * other instant, and the size of the error is the size of the clip's own
 * vertical travel. Writing `k` for his leg-length ratio and `H(t)` for the
 * clip's hip height, the shift gives him `H(t) + (k-1)·rest` where his body
 * wants `H(t)·k`, so the residual is
 *
 *     (k - 1) · (rest - H(t))
 *
 * — zero while he stands, and largest exactly where the hips leave rest
 * furthest. Measured over the shipped pack (scripts/qa-ground-sweep.mjs), the
 * hips drop 833 mm below rest in BicycleKick, 823 in GetUpFront / FallForward /
 * GkSmother, 806 and 787 in the mid dives, 730 in SlideTackle. Against the
 * squad's rest hip heights — 936.3 mm (nakamura, 1.71 m) to 1056.8 mm (vance,
 * 1.93 m) — that puts the keeper 58 mm ABOVE the pitch on a bicycle kick and
 * the winger 43 mm INSIDE it, a 101 mm spread on one clip. Ordinary play is
 * smaller and just as visible: ±14 mm on StandingTackle, ±11 on
 * StrikeForwardJog, ±10 on Jump.
 *
 * ── THE CORRECTION ─────────────────────────────────────────────────────────
 *
 * Multiply, do not add. `H(t)·k` is right at rest AND through the travel, and
 * because his own rest hips ARE `masterRest·k` it needs no second datum: one
 * multiply on one channel, applied to the mixer's OUTPUT rather than to the
 * clips, so twenty-two men of fourteen heights share one clip pack instead of
 * fourteen rescaled copies of it. Blending is linear, so scaling the blended
 * result is identical to scaling every clip and blending after.
 *
 * ⚠ THE VERTICAL AXIS IS Z, NOT Y. `root` carries a -90 deg rotation about X (a
 * Z-up source converted to Y-up) and `hips` is its direct child, so in the hips'
 * own parent space the master's rest position is [0, -0.005, 0.988]. Scaling
 * component 1 multiplies a near-zero horizontal term, changes the render by
 * nothing, and looks like a fix that was tried. Component 2 is the height.
 *
 * ⚠ AND ONLY THE HEIGHT. The horizontal components of this track are the clip's
 * lateral and forward root travel, which `rootMotion` reads to drive the
 * character across the pitch. Scaling those would make tall men cover ground
 * faster than the sim says they do — a desync between what is drawn and what is
 * simulated, which §13 calls the deceptive kind of wrong.
 */

import { AnimationClip, VectorKeyframeTrack, type KeyframeTrack } from 'three';
import { AUTHORED_MASTER_HIPS_REST_Y, ROOT_MOTION_BONE } from './authoredMasterLoader';

/** Index of the hips' own vertical component in its local position. See above. */
export const HIPS_HEIGHT_COMPONENT = 2;

/**
 * Ratios outside this band are not a tall man, they are a broken measurement —
 * a rig in centimetres, a character whose soles are not on his own origin, or a
 * hips bone read before `updateMatrixWorld`. The squad spans 0.948 to 1.070.
 */
const MIN_RATIO = 0.75;
const MAX_RATIO = 1.35;

/** Closer than this to 1 and the multiply is not worth the branch. */
const NEUTRAL_EPS = 1e-4;

/**
 * His leg-length ratio, from his own rest hip height above his soles.
 *
 * Returns 1 — the identity, which costs nothing downstream — when the height is
 * missing or implausible, because a character rendered at the master's
 * proportions is a smaller defect than one rendered at a garbage scale.
 */
export function legScaleFromRestHips(restHipsY: number | null | undefined): number {
  if (typeof restHipsY !== 'number' || !Number.isFinite(restHipsY)) return 1;
  const ratio = restHipsY / AUTHORED_MASTER_HIPS_REST_Y;
  if (!(ratio >= MIN_RATIO && ratio <= MAX_RATIO)) return 1;
  return ratio;
}

/** True when applying this ratio would change the pose at all. */
export function legScaleIsMeaningful(ratio: number): boolean {
  return Math.abs(ratio - 1) > NEUTRAL_EPS;
}

/** The one track in a clip that carries the hips' absolute height. */
const HIPS_TRACK = `${ROOT_MOTION_BONE}.position`;

/**
 * ⚠ WHY THIS IS NOT DONE AFTER `mixer.update` INSTEAD.
 *
 * It was, for about an hour, and it is wrong in a way worth recording because
 * the shape of it recurs. `PropertyMixer.apply` writes to the scene graph ONLY
 * when the accumulated value differs from what it last wrote:
 *
 *     for ( let i = stride, e = stride + stride; i !== e; ++ i )
 *       if ( buffer[ i ] !== buffer[ i + stride ] ) { binding.setValue(...); break; }
 *
 * A standing player's hips height does not change from frame to frame, so after
 * the first write the mixer stops writing — and a multiply applied after it then
 * scales its OWN previous output. Measured on a 60-frame idle: 1.0699 became
 * 56.8, the character 57 metres in the air. Nothing threw and no gate noticed;
 * `hipsLegScale.test.ts` names the case.
 *
 * Scaling the TRACK has no such window. It also matches
 * `scaleHipsHeight` in scripts/qa-anim-cast-page.js exactly, so the instrument
 * that measures the fix and the runtime that ships it do the same arithmetic.
 */
function scaleHipsTrack(track: KeyframeTrack, ratio: number): KeyframeTrack {
  const values = Float32Array.from(track.values);
  for (let i = HIPS_HEIGHT_COMPONENT; i < values.length; i += 3) values[i]! *= ratio;
  return new VectorKeyframeTrack(track.name, Array.from(track.times), Array.from(values));
}

/**
 * The clip pack, refitted to these legs.
 *
 * Returns the SAME map when the ratio is 1, so a character built at the master's
 * height shares the pack rather than copying it.
 *
 * Everything except the hips track is shared by reference. A `KeyframeTrack`
 * holds no mixer state — each `AnimationAction` builds its own interpolant from
 * it — so one track object safely backs fourteen men at fourteen heights, and
 * the per-character cost is one small Float32Array per clip rather than a clone
 * of all 222 tracks.
 */
export function scaleClipsForLegs(
  clips: ReadonlyMap<string, AnimationClip>,
  ratio: number,
): Map<string, AnimationClip> {
  if (!legScaleIsMeaningful(ratio)) return new Map(clips);
  const out = new Map<string, AnimationClip>();
  for (const [name, clip] of clips) {
    const i = clip.tracks.findIndex((t) => t.name === HIPS_TRACK);
    if (i < 0) {
      // No hips track means no absolute height to correct — the character keeps
      // his own bind hips, which is already right. Share the clip.
      out.set(name, clip);
      continue;
    }
    const tracks = clip.tracks.slice();
    tracks[i] = scaleHipsTrack(clip.tracks[i]!, ratio);
    out.set(name, new AnimationClip(clip.name, clip.duration, tracks, clip.blendMode));
  }
  return out;
}
